import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet.markercluster"
import { track } from "../lib/mixpanel"

// Leaflet's default icon URLs point to bundled PNGs that esbuild doesn't
// auto-relocate. Point marker-less RAG pins built via divIcon avoid the
// missing-image flash entirely.
const RAG_COLOR = {
  red:   "#ef4444",
  amber: "#f59e0b",
  green: "#10b981",
  gray:  "#d1d5db"
}

export default class extends Controller {
  static targets = ["container"]
  static values = {
    projects: Array,
    missing: Number
  }

  connect() {
    if (!this.hasContainerTarget) return

    const projects = this.projectsValue || []
    if (projects.length === 0) return

    this._initMap(projects)

    track("Portfolio Map Viewed", {
      plotted_count: projects.length,
      missing_count: this.missingValue
    })
  }

  disconnect() {
    if (this._map) {
      this._map.remove()
      this._map = null
    }
  }

  _initMap(projects) {
    this._map = L.map(this.containerTarget, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: false
    })

    // Esri World Light Gray — light, near-grayscale tiles. Grayscale is the
    // point: RAG-colored pins must stay the dominant visual signal, and
    // colored base tiles (highway shields, parks, water) fight them.
    // Was CARTO Positron until 2026-08-27, when CARTO began burning an
    // "API KEY REQUIRED" watermark into keyless tiles — Esri's equivalent
    // needs no key or account. Base and labels are separate layers here;
    // labels ride ABOVE the pins' base but below the markers.
    // Attribution lives in the legend footer under the map (required by
    // Esri's terms), so Leaflet's on-map control stays suppressed.
    L.tileLayer("https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19,
      maxNativeZoom: 16
    }).addTo(this._map)
    L.tileLayer("https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19,
      maxNativeZoom: 16,
      pane: "overlayPane"
    }).addTo(this._map)

    // chunkedLoading batches marker insertion across animation frames — with
    // 2.5k+ projects the single-shot build froze the tab for seconds before
    // anything appeared (Phelipe 2026-08-27).
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 48,
      chunkedLoading: true,
      chunkInterval: 100,
      chunkDelay: 20,
      removeOutsideVisibleBounds: true
    })

    const markers = projects.map(p => {
      const marker = L.marker([p.lat, p.lng], { icon: this._buildIcon(p.rag) })
      marker.bindPopup(() => this._buildPopupHtml(p), { maxWidth: 280, minWidth: 220 })
      return marker
    })

    // addLayers(array) — the bulk path. addLayer() one at a time re-clusters
    // on every insert, which is what made a big portfolio crawl.
    cluster.addLayers(markers)
    this._map.addLayer(cluster)

    if (markers.length === 1) {
      this._map.setView(markers[0].getLatLng(), 13)
    } else {
      // Bounds straight from the coordinates. L.featureGroup(markers) used to
      // register all 2.5k markers into a second layer purely to measure them
      // — the numbers are identical, the layer was the cost.
      this._map.fitBounds(L.latLngBounds(projects.map(p => [p.lat, p.lng])).pad(0.2))
    }
  }

  // One icon object per RAG colour, reused across every marker — Leaflet is
  // happy to share icons, and building 2.5k identical divIcons was pure waste
  // (Phelipe 2026-08-27).
  _buildIcon(rag) {
    const key = RAG_COLOR[rag] ? rag : "gray"
    this._icons ||= {}
    return (this._icons[key] ||= L.divIcon({
      html: `<span class="portfolio-map-pin" style="background:${RAG_COLOR[key]};"></span>`,
      className: "portfolio-map-pin-wrapper",
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      popupAnchor: [0, -10]
    }))
  }

  _buildPopupHtml(p) {
    const ragLabel = { red: "Behind", amber: "At risk", green: "On track", gray: "No data" }[p.rag] || "—"
    const ragColor = RAG_COLOR[p.rag] || RAG_COLOR.gray

    return `
      <div class="portfolio-map-popup">
        <div class="portfolio-map-popup-title">${this._escape(p.name)}</div>
        <div class="portfolio-map-popup-meta">
          <span class="portfolio-map-popup-dot" style="background:${ragColor};"></span>
          <span>${ragLabel}</span>
          ${p.phase ? `<span class="portfolio-map-popup-sep">·</span><span>${this._escape(p.phase)}</span>` : ""}
        </div>
        ${p.city_state ? `<div class="portfolio-map-popup-row">${this._escape(p.city_state)}</div>` : ""}
        ${p.budget ? `<div class="portfolio-map-popup-row"><span class="portfolio-map-popup-label">Budget</span> ${this._escape(p.budget)}</div>` : ""}
        <a href="${p.url}" class="portfolio-map-popup-link" data-turbo="false">Open project dashboard →</a>
      </div>
    `
  }

  _escape(str) {
    if (str == null) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
